import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentService } from '@galaxy/core';
import { AddressAPIService, CountryConfiguration, CountryOption } from '@vendasta/address';
import { AccountService, AddressInterface, BusinessDataInterface, ContactInterface } from '@vendasta/business-center';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TaxonomyOption, TaxonomyService } from '@vendasta/taxonomy';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, of } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { GooglePlacesService } from '../core/google-places/google-places.service';
import { ImageService } from '../core/image.service';
import { RegistrationCompleteDialogComponent } from './registration-complete-dialog/registration-complete-dialog.component';

const COUNTRY_KEY = 'country';
const REGION_KEY = 'administrative_area_level_1';
const CITY_KEY = 'locality';
const POSTAL_CODE_KEY = 'postal_code';
const ADDRESS_KEYS = ['street_number', 'route', 'premise', 'subpremise'];
const ALTERNATE_ADDRESS_KEYS = {
  BR: ['route', 'street_number', 'premise', 'subpremise'],
  CZ: ['route', 'street_number'],
};
const SHORT_NAME_KEY = 'short_name';
const LONG_NAME_KEY = 'long_name';

@Component({
  selector: 'bc-public-signup',
  templateUrl: './public-signup.component.html',
  styleUrls: ['./public-signup.component.scss'],
  providers: [TaxonomyService],
  standalone: false,
})
export class PublicSignupComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('search', { static: true })
  searchElementRef: ElementRef;
  countryOptions$: Observable<CountryOption[]>;
  countryConfiguration$: Observable<CountryConfiguration>;

  filteredTaxonomies$: Observable<TaxonomyOption[]>;

  private subscriptions: Subscription[] = [];
  private fullFormVisible$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private saving$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private categorySearchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  captchaResponse: string;

  fullFormVisible$: Observable<boolean> = this.fullFormVisible$$.asObservable();
  saving$: Observable<boolean> = this.saving$$.asObservable();
  categorySearchTerm$: Observable<string> = this.categorySearchTerm$$.asObservable();

  formGroup: UntypedFormGroup;
  wl_logo_img$: Observable<string>;
  poweredByGoogleLogo: string;
  categoryUiFormControl = new UntypedFormControl('', Validators.required);
  submitted = false;
  private readonly onDestroy = new Subject<void>();
  constructor(
    private environmentService: EnvironmentService,
    private route: ActivatedRoute,
    private imageService: ImageService,
    private httpClient: HttpClient,
    private fb: UntypedFormBuilder,
    private readonly googlePlacesService: GooglePlacesService,
    private readonly addressService: AddressAPIService,
    private readonly businessAccountService: AccountService,
    private readonly snackbarService: SnackbarService,
    private readonly taxonomyService: TaxonomyService,
    public dialog: MatDialog,
  ) {}

  ngAfterViewInit(): void {
    this.googlePlacesService.init(this.searchElementRef.nativeElement);
  }

  ngOnInit(): void {
    const partnerId = this.route.snapshot.paramMap.get('partnerId');
    const marketId = this.route.snapshot.paramMap.get('marketId');

    this.countryOptions$ = this.addressService.listAllCountryOptions().pipe(
      map((res) => {
        if (!res) {
          return [];
        }
        return res.sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
      }),
    );

    this.filteredTaxonomies$ = combineLatest([
      this.categorySearchTerm$.pipe(startWith<string | TaxonomyOption>('')),
      this.taxonomyService.getTaxonomyIds(),
    ]).pipe(
      map(([searchTerm, taxonomies]: [string, TaxonomyOption[]]) =>
        taxonomies.filter((tax) => {
          if (tax) {
            return tax.fullName.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
          }
          return false;
        }),
      ),
    );

    this.wl_logo_img$ = this.imageService.getPartnerMarketLogoImage(partnerId, marketId);

    this.formGroup = this.fb.group({
      firstName: [''],
      lastName: [''],
      email: ['', Validators.email],
      businessName: [''],
      website: [''],
      address: [''],
      city: [''],
      country: [''],
      stateProv: [''],
      postalCode: [''],
      businessCategory: [''],
      phoneNum: [''],
      facebookUrl: [''],
      twitterUrl: [''],
    });

    this.poweredByGoogleLogo = this.imageService.getImageSrc('powered-by-google-on-white.png');
  }
  get f(): { [key: string]: AbstractControl } {
    return this.formGroup.controls;
  }

  ngOnDestroy(): void {
    this.googlePlacesService.destroy();
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.onDestroy.complete();
  }

  setCountry(selection: MatSelectChange): void {
    this.countryConfiguration$ = this.addressService.getCountryConfiguration(selection.value, undefined);

    this.countryConfiguration$.pipe(takeUntil(this.onDestroy)).subscribe((countryConfig) => {
      if (!countryConfig.zones) {
        this.formGroup.removeControl('stateProv');
      } else if (!this.formGroup.get('stateProv')) {
        this.formGroup.addControl('stateProv', new UntypedFormControl());
      }
    });
  }

  addCategory(event: MatAutocompleteSelectedEvent): void {
    this.formGroup.controls.businessCategory.setValue(event.option.value);
    this.categorySearchTerm$$.next('');
    event.option.deselect();
  }

  removeCategory(): void {
    this.formGroup.controls.businessCategory.setValue('');
    this.categorySearchTerm$$.next('');
  }

  updateCategorySearchTerm(term: string): void {
    this.categorySearchTerm$$.next(term);
  }

  openSuccessDialog(): void {
    this.dialog.open(RegistrationCompleteDialogComponent, {
      maxWidth: '100vw',
      minWidth: '50vw',
    });
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.formGroup.valid) {
      this.saving$$.next(true);
      this.subscriptions.push(
        combineLatest([this.googlePlacesService.googlePlace$, this.fullFormVisible$, this.route.params])
          .pipe(
            switchMap(([googlePlace, fullForm, { partnerId, marketId }]) => {
              marketId = !marketId || !marketId.trim() ? 'default' : marketId;

              if (!googlePlace && fullForm) {
                const { businessData, contactData } = this.businessDataFromForm();
                return this.businessAccountService.register(
                  businessData,
                  contactData,
                  this.formGroup.value.businessName,
                  partnerId,
                  marketId,
                  this.formGroup.value.email,
                  this.captchaResponse,
                );
              }

              if (googlePlace && googlePlace.place_id) {
                const businessData = this.businessDataFromPlace(googlePlace);
                return this.businessAccountService.register(
                  businessData,
                  null,
                  googlePlace.name,
                  partnerId,
                  marketId,
                  this.formGroup.value.email,
                  this.captchaResponse,
                );
              }
              throw new Error();
            }),
          )
          .subscribe(
            () => {
              this.openSuccessDialog();
              this.saving$$.next(false);
            },
            (err) => {
              let errMessage;
              if (err.status === 409) {
                errMessage =
                  'The email address you provided is already in use. \
                                  Please provide a different email address to create your account.';
              } else {
                errMessage = 'There was a problem creating your account. Please try again.';
              }
              this.snackbarService.openErrorSnack(errMessage);
              this.saving$$.next(false);
            },
          ),
      );
    } else {
      Object.keys(this.formGroup.controls).forEach((field) => {
        const control = this.formGroup.get(field);
        if (control instanceof UntypedFormControl) {
          control.markAsTouched({ onlySelf: true });
        }
      });
      this.categoryUiFormControl.markAsTouched({ onlySelf: true });
      this.snackbarService.openErrorSnack('Please enter all the required fields.');
    }
  }

  toggleForm(): void {
    this.googlePlacesService.setGooglePlaces(null);
    this.fullFormVisible$ = of(true);
  }
  toggleGoogleForm(): void {
    this.googlePlacesService.init(this.searchElementRef.nativeElement);
    this.fullFormVisible$ = of(false);
  }

  nullDisplayFn(): void {
    return null;
  }

  private businessDataFromPlace(place: google.maps.places.PlaceResult): BusinessDataInterface {
    const country = this.getAddressComponent(place.address_components, COUNTRY_KEY);
    const region = this.getAddressComponent(place.address_components, REGION_KEY);
    const city = this.getAddressComponent(place.address_components, CITY_KEY);
    const postalCode = this.getAddressComponent(place.address_components, POSTAL_CODE_KEY);

    let addressKeys = ADDRESS_KEYS;
    if (
      Object.prototype.hasOwnProperty.call(country, SHORT_NAME_KEY) &&
      Object.prototype.hasOwnProperty.call(ALTERNATE_ADDRESS_KEYS, country[SHORT_NAME_KEY])
    ) {
      addressKeys = ALTERNATE_ADDRESS_KEYS[country[SHORT_NAME_KEY]];
    }

    const addressParts = [];
    addressKeys.forEach((key) => {
      const addressInfo = this.getAddressComponent(place.address_components, key);
      const addressPart = LONG_NAME_KEY in addressInfo ? addressInfo[SHORT_NAME_KEY] : '';
      if (addressPart !== '') {
        addressParts.push(addressPart);
      }
    });

    const addressData: AddressInterface = {
      address: addressParts.join(' '),
      city: SHORT_NAME_KEY in city ? city[SHORT_NAME_KEY] : '',
      country: SHORT_NAME_KEY in country ? country[SHORT_NAME_KEY] : '',
      state: SHORT_NAME_KEY in region ? region[SHORT_NAME_KEY] : '',
      postalCode: SHORT_NAME_KEY in postalCode ? postalCode[SHORT_NAME_KEY] : '',
    };
    const businessData: BusinessDataInterface = {
      address: addressData,
      businessCategory: 'other',
      phoneNumber: place.formatted_phone_number,
      websiteUrl: place.website,
      facebookUrl: '',
      twitterUrl: '',
    };
    return businessData;
  }

  private businessDataFromForm(): {
    businessData: BusinessDataInterface;
    contactData: ContactInterface;
  } {
    const addressData: AddressInterface = {
      address: this.formGroup.value.address,
      city: this.formGroup.value.city,
      state: this.formGroup.value.stateProv,
      country: this.formGroup.value.country,
      postalCode: this.formGroup.value.postalCode,
    };
    const businessData: BusinessDataInterface = {
      address: addressData,
      businessCategory: this.formGroup.value.businessCategory.id,
      phoneNumber: this.formGroup.value.phoneNum,
      websiteUrl: this.formGroup.value.websiteUrl,
      facebookUrl: this.formGroup.value.facebookUrl,
      twitterUrl: this.formGroup.value.twitterUrl,
    };
    const contactData: ContactInterface = {
      firstName: this.formGroup.value.firstName,
      lastName: this.formGroup.value.lastName,
    };
    return { businessData, contactData };
  }

  private getAddressComponent(addressComponents: any[], type: string): any {
    if (addressComponents) {
      for (const component of addressComponents) {
        if (component['types'].indexOf(type) > -1) {
          return component;
        }
      }
    }
    return {};
  }

  captchaCallback(captchaResponse: string) {
    this.captchaResponse = captchaResponse;
  }
}
